import React, { useState, useEffect, useMemo } from 'react'
import { GoogleApiWrapper, Map, Marker, InfoWindow } from 'google-maps-react'
import { GOOGLE_MAPS_KEY } from 'helpers'
import { config } from 'config'
import { Logo } from '../logo'

export const PointOfSaleMap = ({ route, global }) => {
  const { city: cityName } = route.match.params
  const { cities } = global
  const [fetching, setFetching] = useState(true)
  const [pointOfSales, setPointOfSales] = useState([])
  const [currentLocation, setCurrentLocation] = useState({
    lat: -22.904477711506654,
    lng: -47.05995455716901,
  })

  const city = useMemo(() => cities.find(c => c.name === cityName), [cities, cityName])
  const cityId = city?.id

  useEffect(() => {
    const fetchPointOfSales = async () => {
      if (!cityId) {
        setFetching(false)
        return
      }

      try {
        let url = `${config.API_URL}/pointofsale.public.list/${cityId}`

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
              setCurrentLocation({ lat: coords.latitude, lng: coords.longitude })
              url += `?lat=${coords.latitude}&lng=${coords.longitude}`
              loadData(url)
            },
            () => {
              loadData(url)
            },
          )
        } else {
          loadData(url)
        }
      } catch (err) {
        console.error('Erro ao carregar pontos de venda:', err)
        setFetching(false)
      }
    }

    const loadData = async url => {
      try {
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error('Erro ao carregar pontos de venda')
        }
        const data = await response.json()
        setPointOfSales(data.items || [])
      } catch (err) {
        console.error('Erro ao carregar dados:', err)
      } finally {
        setFetching(false)
      }
    }

    fetchPointOfSales()
  }, [cityId])

  return (
    <div className="login-wrapper">
      <div className="login-box homebox-wide">
        <div className="logo logo-small">
          <Logo isLogin />
        </div>
        <div className="login-title">
          <h3>Mapa de Pontos de Venda</h3>
        </div>
        {fetching ? (
          <div className="row">Carregando...</div>
        ) : (
          <div className="map map-small">
            {pointOfSales.length === 0 ? (
              'Nenhum ponto de venda com geolocalização disponível.'
            ) : (
              <LocationMap currentLocation={currentLocation} pointOfSales={pointOfSales} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const LocationMap = GoogleApiWrapper({ apiKey: GOOGLE_MAPS_KEY })(
  ({ pointOfSales, currentLocation, google }) => {
    let center = currentLocation

    if (pointOfSales.length > 0 && pointOfSales[0].geoLocation) {
      center = pointOfSales[0].geoLocation
    }

    const [showingInfoWindow, setShowingInfoWindow] = useState(false)
    const [activeMarker, setActiveMarker] = useState(null)
    const [selectedInfo, setSelectedInfo] = useState(null)

    const onMarkerClick = (props, marker) => {
      setSelectedInfo(props.info)
      setActiveMarker(marker)
      setShowingInfoWindow(true)
    }

    const onMapClicked = () => {
      if (showingInfoWindow) {
        setActiveMarker(null)
        setShowingInfoWindow(false)
      }
    }

    return (
      <Map
        google={google}
        zoom={15}
        initialCenter={center}
        center={center}
        onClick={onMapClicked}
        onReady={(mapProps, map) => {
          map.setOptions({
            styles: [
              {
                featureType: 'poi.business',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'transit',
                elementType: 'labels.icon',
                stylers: [{ visibility: 'off' }],
              },
            ],
          })
        }}
      >
        {pointOfSales.map(pos => {
          if (!pos.geoLocation || (pos.geoLocation.lat === 0 && pos.geoLocation.lng === 0)) {
            return null
          }

          const color = '#5392f9'
          const icon = `<svg width="30" height="30" viewBox="0 0 459 459" xmlns="http://www.w3.org/2000/svg" fill="${color}"><path d="M433.5,25.5h-408v51h408V25.5z M459,280.5v-51L433.5,102h-408L0,229.5v51h25.5v153h255v-153h102v153h51v-153H459zM229.5,382.5h-153v-102h153V382.5z"/></svg>`

          const infoContent = `<div>
            <h1 class="map-title">${pos.businessName}</h1>
            ${
              pos.streetName
                ? `<h1 class="map-title">${pos.streetName}${
                    pos.streetNumber ? `, Nº ${pos.streetNumber}` : ''
                  }</h1>`
                : ''
            }
          </div>`

          return (
            <Marker
              key={pos.id}
              onClick={onMarkerClick}
              position={pos.geoLocation}
              icon={{ url: `data:image/svg+xml;charset=UTF-8;base64,${window.btoa(icon)}` }}
              info={{ content: infoContent }}
            />
          )
        })}

        <InfoWindow marker={activeMarker} visible={showingInfoWindow}>
          <div dangerouslySetInnerHTML={{ __html: selectedInfo?.content || '' }} />
        </InfoWindow>
      </Map>
    )
  },
)
